import React from 'react';
import { getRem, typographyCaption } from 'lib/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const BADGE_SIZE = getRem(16);

const StyledBadge = styled.div`
  box-sizing: border-box;
  display: inline-block;
  line-height: 0;
`;

const StyledBadgeText = styled.span`
  ${({ theme }) => typographyCaption(theme)};
  align-items: center;
  background-color: ${({ $badgeColor, theme }) => $badgeColor || theme.color.system.negative[500].value};
  border-radius: ${({ theme }) => theme.size.borderRadius.large.value};
  box-sizing: border-box;
  color: ${({ $textColor, theme }) => $textColor || theme.color.additional.alwaysWhite.value};
  display: inline-flex;
  justify-content: center;
  line-height: normal;
  min-height: ${BADGE_SIZE};
  min-width: ${BADGE_SIZE};
  padding: 0 ${({ theme }) => theme.size.spacing.small.value};
  white-space: nowrap;
`;

const Badge = ({
  badgeColor = undefined,
  children = null,
  dataTestId = '',
  id = undefined,
  textColor = undefined,
  ...other
}) => (
  <StyledBadge data-testid={dataTestId} {...other}>
    <StyledBadgeText
      data-testid={dataTestId ? `${dataTestId}-text` : undefined}
      $badgeColor={badgeColor}
      id={id}
      $textColor={textColor}
      {...other}
    >
      {children}
    </StyledBadgeText>
  </StyledBadge>
);

// NOTE: aria-label can be passed to Badge and it will be added to the DOM element. When we used defaultProps, aria-label was defaulted to '', but switching to inline props meant we couldn't set its default. You can't pass in props with dashes. We could change it to 'ariaLabel' but that would have been a breaking change.
Badge.propTypes = {
  /** Sets background color of Badge component */
  badgeColor: PropTypes.string,
  /** Any content inserted between the tags */
  children: PropTypes.node,
  /** Unique identifier of the component, required if used in 'withBadge' component. */
  id: PropTypes.string,
  /** Sets the color of the text in badge */
  textColor: PropTypes.string,
  /** ID attribute to test the node */
  dataTestId: PropTypes.string,
};

export { Badge };
